import type { LegendListRef } from '@legendapp/list/react-native';
import type { FC, PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useMemo, useRef } from 'react';

import { useMeinOrtChannels } from '@/features/mein-ort/components/meinOrtOptions';
import { useMeinOrtScroll } from '@/features/mein-ort/components/MeinOrtScrollContext';
import type { MeinOrtChannelConfig } from '@/features/mein-ort/components/meinOrtTypes';
import {
  MeinOrtChannel,
  MeinOrtTab
} from '@/features/mein-ort/components/meinOrtTypes';
import { useMeinOrtChannelTeasers } from '@/features/mein-ort/hooks/useMeinOrtChannelTeasers';
import { useBadgeStore } from '@/shared/store/useBadgeStore';

export type MeinOrtTabContextValue = {
  activeChannel: MeinOrtChannel;
  activeTab: MeinOrtTab;
  channelConfigs: MeinOrtChannelConfig[];

  onChannelChange: (channel: MeinOrtChannel) => void;
  onTabChange: (tab: MeinOrtTab) => void;
  setPostFeedRef: (ref: LegendListRef | null) => void;
};

export const MeinOrtTabContext = createContext<
  MeinOrtTabContextValue | undefined
>(undefined);

type MeinOrtTabProviderProps = {
  routeActiveChannel?: MeinOrtChannel;
  routeTab?: MeinOrtTab;
  onVisit: (params: { channel: MeinOrtChannel; tab: MeinOrtTab }) => void;
};

export const MeinOrtTabProvider: FC<
  PropsWithChildren<MeinOrtTabProviderProps>
> = ({
  children,
  routeActiveChannel = MeinOrtChannel.FUNKZENTRALE,
  routeTab = MeinOrtTab.ALLE,
  onVisit
}) => {
  const channelConfigs: MeinOrtChannelConfig[] = useMeinOrtChannels();
  const activeChannelConfig = channelConfigs.find(
    (config) => config.key === routeActiveChannel
  );
  const activeTab = activeChannelConfig?.tabs.some(
    (tab) => tab.key === routeTab
  )
    ? routeTab
    : (activeChannelConfig?.tabs[0]?.key ?? MeinOrtTab.ALLE);
  const { scrollY } = useMeinOrtScroll();
  const postFeedRef = useRef<LegendListRef | null>(null);
  const teasers = useMeinOrtChannelTeasers(true);
  const markPostsAsRead = useBadgeStore((state) => state.markPostsAsRead);

  const setPostFeedRef = useCallback((ref: LegendListRef | null) => {
    postFeedRef.current = ref;
  }, []);

  const markTeaserPostsForTab = useCallback(
    (tab: MeinOrtTab) => {
      if (tab !== MeinOrtTab.ALLE) return;
      const postIds = [
        ...(teasers.news.posts ?? []),
        ...(teasers.plausch.posts ?? []),
        ...(teasers.markt.posts ?? []),
        ...(teasers.events.posts ?? [])
      ].map((p) => p.postId);
      if (postIds.length > 0) markPostsAsRead(postIds);
    },
    [teasers, markPostsAsRead]
  );

  const onChannelChange = useCallback(
    (channel: MeinOrtChannel) => {
      if (channel === routeActiveChannel) return;
      const next = channelConfigs.find((c) => c.key === channel);
      if (!next) return;
      scrollY.value = 0;
      postFeedRef.current?.scrollToOffset({ offset: 0, animated: false });
      const targetTab = next.tabs[0].key;
      markTeaserPostsForTab(targetTab);
      onVisit({ channel, tab: targetTab });
    },
    [
      channelConfigs,
      scrollY,
      routeActiveChannel,
      onVisit,
      markTeaserPostsForTab
    ]
  );

  const onTabChange = useCallback(
    (tab: MeinOrtTab) => {
      if (tab === routeTab) return;
      scrollY.value = 0;
      postFeedRef.current?.scrollToOffset({ offset: 0, animated: false });
      markTeaserPostsForTab(tab);
      onVisit({ channel: routeActiveChannel, tab });
    },
    [scrollY, routeActiveChannel, routeTab, onVisit, markTeaserPostsForTab]
  );

  const context = useMemo<MeinOrtTabContextValue>(
    () => ({
      activeChannel: routeActiveChannel,
      activeTab,
      channelConfigs,
      onChannelChange,
      onTabChange,
      setPostFeedRef
    }),
    [
      routeActiveChannel,
      activeTab,
      channelConfigs,
      onChannelChange,
      onTabChange,
      setPostFeedRef
    ]
  );

  return <MeinOrtTabContext value={context}>{children}</MeinOrtTabContext>;
};

export const useMeinOrtTab = () => {
  const context = useContext(MeinOrtTabContext);
  if (context === undefined) {
    throw new Error('useMeinOrtTab must be used within MeinOrtTabContext');
  }
  return context;
};
